import { FC, useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import DarkBackgroundLoadingPanel from 'src/components/DarkBackgroundLoadingPanel';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import Button, { isButtonDisabled } from 'src/components/design-system/Button';
import PopUp from 'src/components/design-system/PopUp';
import useConfirm from 'src/hooks/useConfirm';
import { useContextSelector } from 'use-context-selector';
import { InvoiceContext } from './InvoiceDetailsContext';
import useInvoiceButtonState from './useInvoiceButtonState';

interface SaveInvoiceProps {}

const SaveInvoice: FC<SaveInvoiceProps> = () => {
  const [, setInvoiceLastSavedDate] = useState<null | Date>(null);
  const { confirm } = useConfirm();
  const [, setErrorCount] = useState(0);
  const refreshPage = useContextSelector(InvoiceContext, (value) => value.refreshPage);

  const isInvoiceDirty = useContextSelector(InvoiceContext, (value) => value.isInvoiceDirty);
  const setIsGlCodesDirty = useContextSelector(InvoiceContext, (value) => value.setIsGlCodesDirty);
  const hasErrors = useContextSelector(InvoiceContext, (value) => value.hasErrors);
  const errorList = useContextSelector(InvoiceContext, (value) => value.errorList);

  const title = useContextSelector(InvoiceContext, (value) => value.title);
  const saveInvoice = useContextSelector(InvoiceContext, (value) => value.saveInvoice);
  const isInvoiceAPIDDirty = useContextSelector(
    InvoiceContext,
    (value) => value.isInvoiceAPIDDirty
  );
  const isUpdatingInvoice = useContextSelector(InvoiceContext, (value) => value.isUpdatingInvoice);
  const isInvoiceSupplierNameDirty = useContextSelector(
    InvoiceContext,
    (value) => value.isInvoiceSupplierNameDirty
  );
  const isInherited = useContextSelector(InvoiceContext, (value) => value.isInherited);

  const singleGlPeriodStartDate = useContextSelector(
    InvoiceContext,
    (value) => value.singleGlPeriodStartDate
  );

  const setSelectedLineItemIds = useContextSelector(
    InvoiceContext,
    (value) => value.setSelectedLineItemIds
  );

  const [messages, buttonState] = useInvoiceButtonState('Update');

  const updateInvoice = useCallback(
    (isAutoSave: boolean) => {
      if (!hasErrors && isInvoiceDirty) {
        setErrorCount((prev) => {
          try {
            if (!isAutoSave || prev < 3) {
              saveInvoice(singleGlPeriodStartDate).then(async () => {
                setIsGlCodesDirty(false);
                setInvoiceLastSavedDate(new Date());
                setSelectedLineItemIds([]);
                if (isAutoSave) {
                  toast.success(`${title} Autosaved successfully!`);
                } else {
                  toast.success(`${title} saved successfully!`);
                }
                await refreshPage();
              });
            }
          } catch (error) {
            console.error(error);
            if (isAutoSave) {
              return prev + 1;
            }
          }
          return prev;
        });
      }
    },
    [
      hasErrors,
      isInvoiceDirty,
      refreshPage,
      saveInvoice,
      setIsGlCodesDirty,
      singleGlPeriodStartDate,
      setSelectedLineItemIds,
      title,
    ]
  );

  const handleClick = useCallback(
    async (isAutoSave: boolean) => {
      if (isInherited && (isInvoiceAPIDDirty || isInvoiceSupplierNameDirty)) {
        let warningText = '';
        if (isInvoiceAPIDDirty && isInvoiceSupplierNameDirty) {
          warningText = 'AP ID and External Supplier Name';
        } else if (isInvoiceAPIDDirty) {
          warningText = 'AP ID';
        } else if (isInvoiceSupplierNameDirty) {
          warningText = 'External Supplier Name';
        }
        confirm({
          title: 'AP ID and External Supplier Name Warning',
          content: (
            <div className="min-w-[240px]">
              <div>
                You have changed the {warningText} which will cause the inheritance to break from
                Management Company. Continue if you are okay with the change and wish to save the
                value at the hotel level
              </div>
            </div>
          ),
          cancelButtonText: 'Close',
          confirmButtonText: 'Continue',
          cancelColor: 'info',
        }).then((res) => {
          if (res) {
            updateInvoice(isAutoSave);
          }
        });
      } else {
        updateInvoice(isAutoSave);
      }
    },
    [confirm, isInherited, isInvoiceAPIDDirty, isInvoiceSupplierNameDirty, updateInvoice]
  );

  const displayMessages = useMemo(() => {
    let msgs: string[] = errorList;
    if (isButtonDisabled(buttonState)) {
      msgs = [...msgs, ...messages];
    }
    return msgs;
  }, [buttonState, errorList, messages]);

  return (
    <div className="w-[200px] flex flex-row items-center justify-start">
      <PopUp
        show={isButtonDisabled(buttonState) || displayMessages?.length > 0}
        content={
          <div className="m-1 max-w-[300px]">
            <PopUpContentWrapper type="Error" contents={displayMessages} />
          </div>
        }
      >
        <Button
          onClick={() => handleClick(false)}
          disabled={
            hasErrors || !isInvoiceDirty
            // areLineItemsThrottling ||
            // areBudgetsThrottling ||
            // areGLCodesThrottling ||
            // areLineItemsDebouncing
          }
          loading={isUpdatingInvoice}
          buttonState={buttonState}
        >
          Save
        </Button>
      </PopUp>
      {isUpdatingInvoice && <DarkBackgroundLoadingPanel id="save-invoice" />}
    </div>
  );
};

export default SaveInvoice;
